import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, CheckCircle2, Clock, FileText, ExternalLink, ArrowRight, BookOpen, BarChart3, Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import { api, API } from "../../lib/api";
import { trackClick, annuityReviewUrl, AUGUSTA_URL } from "../../lib/crossSell";
import { AugustaFeatureBlock } from "../../components/wealth/AugustaPromo";

/**
 * Retirement Resource Center / Toolkit Library — the single hub for paid
 * Toolkit buyers. Lists ALL 7 items (personalized Scorecard + 6 PDF guides),
 * plus the two cross-sell CTAs (Complimentary Annuity Review + Precious
 * Metals education). Access is gated server-side by lead_id+purchase.
 */
export default function ToolkitLibrary() {
    const [params] = useSearchParams();
    const leadId = params.get("lead_id");
    const [deliverables, setDeliverables] = useState(null);
    const [lead, setLead] = useState(null);
    const [status, setStatus] = useState(leadId ? "loading" : "no_lead");

    useEffect(() => {
        if (!leadId) return;
        let alive = true;
        (async () => {
            try {
                const [delResp, leadResp] = await Promise.all([
                    api.get(`/wealth/toolkit/deliverables/${leadId}`),
                    api.get(`/wealth/result/${leadId}`).catch(() => ({ data: null })),
                ]);
                if (!alive) return;
                setDeliverables(delResp.data.deliverables);
                setLead(leadResp.data);
                setStatus("ready");
            } catch (e) {
                if (alive) setStatus(e?.response?.status === 403 ? "forbidden" : "error");
            }
        })();
        return () => { alive = false; };
    }, [leadId]);

    if (status === "no_lead") {
        return (
            <div className="min-h-[70vh] flex items-center justify-center bg-[#fdfbf6] px-6">
                <div className="text-center max-w-md" data-testid="library-no-lead">
                    <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto" />
                    <h2 className="mt-6 font-serif text-2xl text-emerald-950 font-semibold">Open from your purchase email</h2>
                    <p className="mt-3 text-sm text-emerald-900">
                        Your Toolkit Library link is in your purchase confirmation email. Open that email
                        and click "Open my Toolkit downloads" — it includes your secure access link.
                    </p>
                    <Link to="/wealth" className="mt-6 inline-flex items-center gap-2 text-emerald-900 hover:text-emerald-700 font-semibold text-sm">
                        Back to Get Your Wealth Report <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        );
    }

    if (status === "loading") {
        return (
            <div className="min-h-[70vh] flex items-center justify-center bg-[#fdfbf6]" data-testid="library-loading">
                <Loader2 className="w-10 h-10 text-emerald-700 animate-spin" />
            </div>
        );
    }

    if (status === "forbidden") {
        return (
            <div className="min-h-[70vh] flex items-center justify-center bg-[#fdfbf6] px-6">
                <div className="text-center max-w-md" data-testid="library-forbidden">
                    <AlertTriangle className="w-10 h-10 text-rose-600 mx-auto" />
                    <h2 className="mt-6 font-serif text-2xl text-emerald-950 font-semibold">Toolkit not purchased</h2>
                    <p className="mt-3 text-sm text-emerald-900">
                        Your Toolkit Library unlocks after you purchase the Retirement Readiness Toolkit™ ($29.95).
                    </p>
                    <Link to="/wealth/assessment" className="mt-6 inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-amber-300 px-5 py-3 rounded-lg font-semibold text-sm">
                        Start my Scorecard · $29.95 <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="min-h-[70vh] flex items-center justify-center bg-[#fdfbf6] px-6">
                <div className="text-center max-w-md" data-testid="library-error">
                    <AlertTriangle className="w-10 h-10 text-amber-600 mx-auto" />
                    <p className="mt-5 text-emerald-900/80">We hit a snag loading your Toolkit. Refresh, or email reviews@yourannuityreview.com.</p>
                </div>
            </div>
        );
    }

    const firstName = (lead?.full_name || "").split(" ")[0];
    const hasScore = Boolean(lead?.wealth_report_score?.overall);
    // Total = personalized Scorecard (1) + the PDF deliverables (currently 6).
    // The Scorecard counts as "unlocked" only after the buyer completes their assessment.
    const pdfAvailable = deliverables ? deliverables.filter((d) => d.status === "available").length : 0;
    const availableCount = (hasScore ? 1 : 0) + pdfAvailable;
    const totalCount = 1 + (deliverables ? deliverables.length : 0);

    return (
        <div className="bg-[#fdfbf6] py-16 px-4" data-testid="toolkit-library">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold tracking-wide">
                        <ShieldCheck className="w-3.5 h-3.5" /> RETIREMENT RESOURCE CENTER
                    </div>
                    <h1 className="mt-5 font-serif text-4xl sm:text-5xl text-emerald-950 font-semibold leading-tight">
                        {firstName ? `${firstName}'s ` : "Your "}Toolkit Library
                    </h1>
                    <p className="mt-4 text-base text-emerald-900 max-w-2xl mx-auto leading-relaxed" data-testid="library-tagline">
                        Your complete Retirement Readiness Toolkit&trade; — your personalized Scorecard plus six downloadable educational guides. Bookmark this page; new guides unlock here automatically.
                    </p>
                </div>

                {/* Unlocked value stat strip */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10" data-testid="library-stats">
                    <div className="bg-white rounded-xl border border-emerald-900/10 px-4 py-4 text-center">
                        <div className="font-serif text-3xl text-emerald-950 font-semibold leading-none">{availableCount}<span className="text-emerald-800 text-xl">/{totalCount}</span></div>
                        <div className="mt-1.5 text-[10px] uppercase tracking-[0.16em] text-emerald-900 font-bold">Resources unlocked</div>
                    </div>
                    <div className="bg-white rounded-xl border border-emerald-900/10 px-4 py-4 text-center">
                        <div className="font-serif text-3xl text-emerald-950 font-semibold leading-none">1<span className="text-emerald-800 text-xl">+10</span></div>
                        <div className="mt-1.5 text-[10px] uppercase tracking-[0.16em] text-emerald-900 font-bold">Scorecard + PDFs</div>
                    </div>
                    <div className="bg-white rounded-xl border border-emerald-900/10 px-4 py-4 text-center">
                        <div className="font-serif text-3xl text-emerald-950 font-semibold leading-none">$549</div>
                        <div className="mt-1.5 text-[10px] uppercase tracking-[0.16em] text-emerald-900 font-bold">Complete value</div>
                    </div>
                    <div className="bg-white rounded-xl border border-emerald-900/10 px-4 py-4 text-center">
                        <div className="font-serif text-3xl text-amber-700 font-semibold leading-none">PAID</div>
                        <div className="mt-1.5 text-[10px] uppercase tracking-[0.16em] text-emerald-900 font-bold">Lifetime access</div>
                    </div>
                </div>

                {/* Scorecard tile */}
                <div className="bg-white rounded-2xl border border-emerald-900/10 shadow-xl shadow-emerald-900/5 p-8 mb-6" data-testid="library-scorecard">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex gap-4 flex-1 min-w-0">
                            <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-emerald-900 text-amber-300 flex items-center justify-center">
                                <BarChart3 className="w-7 h-7" />
                            </div>
                            <div className="min-w-0">
                                <div className="text-[11px] uppercase tracking-[0.22em] text-amber-700 font-bold">Your personalized result</div>
                                <h2 className="mt-1 font-serif text-2xl text-emerald-950 font-semibold">Retirement Readiness Scorecard&trade;</h2>
                                {hasScore ? (
                                    <p className="mt-1.5 text-sm text-emerald-900">Overall score <strong className="text-emerald-950">{lead.wealth_report_score.overall}</strong> · {lead.wealth_report_score.tier}. Open to review your sub-scores and recommendations.</p>
                                ) : (
                                    <p className="mt-1.5 text-sm text-emerald-900">Complete your 2-step assessment to unlock your personalized scorecard.</p>
                                )}
                            </div>
                        </div>
                        <Link
                            to={hasScore ? `/wealth/results?lead_id=${leadId}` : `/wealth/assessment?from_toolkit=${leadId}`}
                            data-testid="library-scorecard-cta"
                            className="inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-amber-300 px-5 py-2.5 rounded-lg font-semibold text-sm"
                        >
                            {hasScore ? "Open scorecard" : "Complete assessment"} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>

                {/* PDF deliverables */}
                <div className="bg-white rounded-2xl border border-emerald-900/10 shadow-xl shadow-emerald-900/5 p-8 mb-6" data-testid="library-deliverables">
                    <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                        <div className="flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-emerald-800" />
                            <h2 className="font-serif text-2xl text-emerald-950 font-semibold">Downloadable Guides</h2>
                        </div>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-bold tracking-wide">
                            <FileText className="w-3.5 h-3.5" /> {deliverables.length} expert-built PDFs included
                        </span>
                    </div>
                    <p className="text-sm text-emerald-900 mb-2">2 in-depth guides · 3 audit checklists · 1 fill-in worksheet — all printable, all yours to keep.</p>
                    <p className="text-xs text-emerald-800 mb-6">Educational PDF resources. Print, save, or forward. Includes downloadable educational resources only — not personalized financial advice.</p>
                    <ul className="divide-y divide-emerald-900/5">
                        {deliverables.map((d) => {
                            const isAvailable = d.status === "available";
                            const dlUrl = isAvailable ? `${API}/wealth/toolkit/download/${leadId}/${d.id}` : null;
                            return (
                                <li key={d.id} className="py-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4" data-testid={`library-deliverable-${d.id}`}>
                                    <div className="flex gap-4 flex-1 min-w-0">
                                        <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-serif font-bold ${isAvailable ? "bg-emerald-900 text-amber-300" : "bg-emerald-900/8 text-emerald-800"}`}>{d.number}</div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold text-emerald-950 text-base">{d.title}</span>
                                                {isAvailable ? (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold tracking-wide uppercase">
                                                        <CheckCircle2 className="w-3 h-3" /> Available
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[10px] font-bold tracking-wide uppercase">
                                                        <Clock className="w-3 h-3" /> Coming Soon
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-1.5 text-sm text-emerald-900 leading-relaxed">{d.description}</p>
                                            <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-emerald-800 font-medium">
                                                <FileText className="w-3 h-3" /> {d.format} download
                                            </div>
                                            {isAvailable && d.affiliate_url && (
                                                <a
                                                    href={d.affiliate_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer sponsored"
                                                    onClick={() => trackClick("augusta", { source_page: "toolkit_library", lead_id: leadId, deliverable_id: d.id })}
                                                    className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-700 hover:text-amber-900 underline underline-offset-2 decoration-amber-300 hover:decoration-amber-700 transition-colors"
                                                    data-testid={`library-affiliate-${d.id}`}
                                                >
                                                    <ExternalLink className="w-3 h-3" /> {d.affiliate_label || "Learn More About Gold and Silver IRAs"}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    <div className="sm:self-center flex-shrink-0">
                                        {isAvailable ? (
                                            <a
                                                href={dlUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-amber-300 px-4 py-2.5 rounded-lg font-semibold text-sm shadow-md transition-colors"
                                                data-testid={`library-download-${d.id}`}
                                            >
                                                <Download className="w-4 h-4" /> Download
                                            </a>
                                        ) : (
                                            <span className="inline-flex items-center gap-2 bg-emerald-900/5 text-emerald-800 px-4 py-2.5 rounded-lg font-semibold text-sm cursor-not-allowed">
                                                <Clock className="w-4 h-4" /> Coming Soon
                                            </span>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* Complimentary Annuity Review CTA */}
                <a
                    href={annuityReviewUrl({ leadId })}
                    onClick={() => trackClick("annuity_review", { source_page: "toolkit_library", lead_id: leadId })}
                    className="block mb-6 p-8 rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-200 hover:border-amber-300 transition-colors"
                    data-testid="library-annuity-review-cta"
                >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-800 font-bold">Complimentary next step</div>
                            <h3 className="mt-2 font-serif text-2xl text-emerald-950 font-semibold">Request a Complimentary Annuity Review</h3>
                            <p className="mt-2 text-sm text-emerald-900/80 max-w-2xl leading-relaxed">
                                A licensed advisor walks through your specific annuity contract or retirement plan line by line in about 15 minutes. No pressure. No follow-up sales calls. Already included with your Toolkit purchase.
                            </p>
                        </div>
                        <span className="inline-flex items-center gap-2 bg-emerald-900 text-amber-300 px-5 py-3 rounded-lg font-semibold text-sm shadow-md">
                            Claim my free review <ArrowRight className="w-4 h-4" />
                        </span>
                    </div>
                </a>

                {/* Augusta — high-prominence partner feature (replaces the old subtle card) */}
                <AugustaFeatureBlock />

                {/* Compliance disclaimer */}
                <div className="text-xs text-emerald-900 leading-relaxed border-t border-emerald-900/10 pt-6" data-testid="library-disclaimer">
                    <strong className="text-emerald-900/80">Disclosure:</strong> Educational and informational purposes only. Not financial, tax, legal, investment, or insurance advice. Augusta Precious Metals link contains an affiliate identifier; the publisher may receive compensation if you choose to engage their services. Consult a licensed professional for advice tailored to your individual circumstances.
                </div>
            </div>
        </div>
    );
}
